import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/JWTAuthContext';
import REQUEST from '../Services/Request';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3, 0),
}));

const OrientationsListings = () => {
  const { user } = useAuth();
  const [orientations, setOrientations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    salesId: '',
    location: '',
    isInterested: '',
    startDate: '',
    endDate: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrientation, setSelectedOrientation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchOrientations();
  }, [filters, pagination.pageIndex, pagination.pageSize]);

  const fetchOrientations = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        PageIndex: pagination.pageIndex.toString(),
        PageSize: pagination.pageSize.toString(),
        ...(filters.firstName && { FirstName: filters.firstName }),
        ...(filters.lastName && { LastName: filters.lastName }),
        ...(filters.email && { Email: filters.email }),
        ...(filters.phoneNumber && { PhoneNumber: filters.phoneNumber }),
        ...(filters.salesId && { SalesId: filters.salesId }),
        ...(filters.location && { Location: filters.location }),
        ...(filters.isInterested !== '' && { IsInterested: filters.isInterested }),
        ...(filters.startDate && { StartDate: filters.startDate }),
        ...(filters.endDate && { EndDate: filters.endDate }),
      });

      const response = await REQUEST({
        method: "GET",
        url: `feedback/orientation?${queryParams.toString()}`,
      });

      setOrientations(response.Data || []);
      setPagination(prev => ({
        ...prev,
        totalCount: response.TotalCount || 0,
        totalPages: response.TotalPages || 0,
      }));
    } catch (error) {
      console.error('Error fetching orientations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load orientations',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: newPage + 1,
    }));
  };

  const handlePageSizeChange = (event) => {
    setPagination(prev => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 1,
    }));
  };

  const handleSearch = () => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 1,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      salesId: '',
      location: '',
      isInterested: '',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({
      ...prev,
      pageIndex: 1,
    }));
  };

  const handleViewOrientation = (orientation) => {
    setSelectedOrientation(orientation);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrientation(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getInterestChip = (isInterested) => {
    if (isInterested === null || isInterested === undefined) {
      return <Chip label="Not Set" color="default" size="small" />;
    }
    return (
      <Chip 
        label={isInterested ? 'Interested' : 'Not Interested'} 
        color={isInterested ? 'success' : 'error'} 
        size="small" 
      />
    );
  };

  return (
    <Container maxWidth="xl">
      <StyledPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Orientation Feedback
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchOrientations}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => console.log('Add new orientation')}
            >
              New Orientation
            </Button> */}
          </Box>
        </Box>

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Search & Filters</Typography>
              <Button
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                sx={{ ml: 'auto' }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </Box>
            
            {showFilters && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={filters.firstName}
                      onChange={handleFilterChange}
                      placeholder="Search by first name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={filters.lastName}
                      onChange={handleFilterChange}
                      placeholder="Search by last name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={filters.email}
                      onChange={handleFilterChange}
                      placeholder="Search by email"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phoneNumber"
                      value={filters.phoneNumber}
                      onChange={handleFilterChange}
                      placeholder="Search by phone"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Sales ID"
                      name="salesId"
                      value={filters.salesId}
                      onChange={handleFilterChange}
                      placeholder="Search by sales ID"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Location"
                      name="location"
                      value={filters.location}
                      onChange={handleFilterChange}
                      placeholder="Search by location"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Interest Status</InputLabel>
                      <Select
                        name="isInterested"
                        value={filters.isInterested}
                        onChange={handleFilterChange}
                        label="Interest Status"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Interested</MenuItem>
                        <MenuItem value="false">Not Interested</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      name="startDate"
                      type="date"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="End Date"
                      name="endDate"
                      type="date"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Data Grid */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Data Grid Table */}
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Contact</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Sales ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Location</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Interest</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Created At</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orientations.map((orientation) => (
                        <tr key={orientation.Id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}>{orientation.Id}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ fontWeight: 'bold' }}>
                              {orientation.FirstName} {orientation.LastName}
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div>{orientation.Email}</div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {orientation.PhoneNumber}
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>{orientation.SalesId}</td>
                          <td style={{ padding: '12px' }}>{orientation.Location || 'N/A'}</td>
                          <td style={{ padding: '12px' }}>{getInterestChip(orientation.IsInterested)}</td>
                          <td style={{ padding: '12px' }}>
                            {new Date(orientation.CreatedAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewOrientation(orientation)}
                              title="View Details"
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>

                {orientations.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No orientation feedback found
                    </Typography>
                  </Box>
                )}

                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={pagination.totalCount}
                  page={pagination.pageIndex - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={pagination.pageSize}
                  onRowsPerPageChange={handlePageSizeChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </StyledPaper>

      {/* Orientation Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Orientation Feedback Details</DialogTitle>
        <DialogContent>
          {selectedOrientation && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                <Typography variant="body1">{selectedOrientation.Id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Sales ID</Typography>
                <Typography variant="body1">{selectedOrientation.SalesId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">First Name</Typography>
                <Typography variant="body1">{selectedOrientation.FirstName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Last Name</Typography>
                <Typography variant="body1">{selectedOrientation.LastName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedOrientation.Email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
                <Typography variant="body1">{selectedOrientation.PhoneNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                <Typography variant="body1">{selectedOrientation.Location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Interest Status</Typography>
                {getInterestChip(selectedOrientation.IsInterested)}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Feedback</Typography>
                <Typography variant="body1">
                  {selectedOrientation.Feedback || 'No feedback provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                <Typography variant="body1">
                  {new Date(selectedOrientation.CreatedAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                <Typography variant="body1">
                  {new Date(selectedOrientation.UpdatedAt).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrientationsListings; 